import { Component, OnInit, Input, ElementRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';
import moment from 'moment';

import {
  AuthenticationService,
  ProfileService,
  CompanyService,
  SettingService,
  FiscalYearService,
  UserNotificationService,
  UserChecklistService,
  SocketService,
  StaffService
} from '../../_services';
import { Company, User } from '../../_models';
import { BaseComponent } from '../../_components/base.component';
import { COMPANY_NAME, GRANTS_BUREAU_ID, GRANTS_BUREAU_NAME } from 'app/app.constants';

import { ConfirmationModalComponent } from 'app/_components/confirmation-modal/confirmation-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BkContextSwitcherComponent } from './widgets/context-switcher/bk-context-switcher.component';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-site-header',
  templateUrl: './site-header.component.html',
  styleUrls: ['./site-header.component.scss']
})
export class SiteHeaderComponent extends BaseComponent implements OnInit {
  COMPANY_NAME = COMPANY_NAME;
  GRANTS_BUREAU_ID = GRANTS_BUREAU_ID;
  GRANTS_BUREAU_NAME = GRANTS_BUREAU_NAME;
  changeEventSubscription: Subscription;
  user: User = new User();
  @Input() companies: Company[];

  /**
   * "Go to Sandbox" in the account menu. Staff always get it; a client only
   * when staff turned the sandbox on for their current company
   * (Company.sandbox_enabled, set in the live admin). Never shown on the
   * sandbox itself (the banner has "Back to live" instead) or in builds
   * without a sandbox URL.
   */
  get sandboxUrl(): string | null {
    if (environment.isSandbox || !environment.sandboxUrl) return null;
    if (this.authenticationService.is_staff()) return environment.sandboxUrl;
    const company = this.getCompany();
    return company?.sandbox_enabled ? environment.sandboxUrl : null;
  }
  @ViewChild(BkContextSwitcherComponent) contextSwitcher?: BkContextSwitcherComponent;
  search: string;
  unreadNotifications: any[];
  unreadNotificationsCount: number;
  userChecklist: any[];
  mobileModal: 'notifications' | 'tasks' | null = null;
  notification: any;
  fiscalYearId: string = '';

  constructor(
    router: Router,
    authenticationService: AuthenticationService,
    companyService: CompanyService,
    private profileService: ProfileService,
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private location: Location,
    private settingService: SettingService,
    private modalService: BsModalService,
    private fiscalYearService: FiscalYearService,
    private userNotificationService: UserNotificationService,
    private userChecklistService: UserChecklistService,
    private socketService: SocketService,
    private toastr: ToastrService,
    private staffService: StaffService,
  ) {
    super(router, authenticationService, companyService);
    this.unreadNotifications = [];
    this.unreadNotificationsCount = 0;
  }

  /** Close mobile slide-down nav + overlays so taps aren't blocked after route changes. */
  private closeMobileChrome(): void {
    const root = this.elementRef.nativeElement as HTMLElement;
    const nav = root.querySelector('#navbarWrapper') as HTMLElement | null;
    if (nav) {
      try {
        const Collapse = (window as any).bootstrap?.Collapse;
        if (Collapse?.getOrCreateInstance) {
          Collapse.getOrCreateInstance(nav, { toggle: false }).hide();
        } else {
          nav.classList.remove('show', 'collapsing');
        }
      } catch {
        nav.classList.remove('show', 'collapsing');
      }
    }
    root.querySelectorAll('.navbar-toggler').forEach((toggler) => {
      toggler.setAttribute('aria-expanded', 'false');
    });
    this.mobileModal = null;
    this.contextSwitcher?.closeMobile();
    this.clearOrphanedModalBackdrops();
  }

  /**
   * ngx-bootstrap sometimes leaves a full-screen .modal-backdrop after a modal
   * closes or a route change. That layer sits above the page (and the hamburger)
   * and swallows every tap — matching "can't click anything" on mobile.
   */
  private clearOrphanedModalBackdrops(): void {
    if (document.querySelector('.modal.show')) {
      return;
    }
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.querySelectorAll('body > .modal-backdrop').forEach((el) => el.remove());
  }

  /** Runs before Bootstrap toggles the collapse; clears tap-blocking leftovers. */
  onMobileNavToggle(): void {
    this.clearOrphanedModalBackdrops();
    this.mobileModal = null;
    this.contextSwitcher?.closeMobile();
  }

  ngOnInit() {
    /* this.socketService.chatNotificationEvent.subscribe(notification => {
      this.notification = notification;
      this.toastr.info(`${moment(this.notification.when).format('MM/DD/YYYY, hh:mm A')}`, `New Message from ${this.notification.from.first_name}`, { closeButton: true, timeOut: 100000 })
        .onTap
        .pipe()
        .subscribe(() => this.router.navigate([`/chat/${this.notification.chat_room_id}`]));
    }); */
    this.userChecklist = [];
    // Close the mobile nav menu (Bootstrap collapse) after navigating to a page.
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      this.closeMobileChrome();
    });
    this.route.queryParams.subscribe(params => {
      if (params.search) {
        this.search = params.search;
      }
    });
    this.profileService.getProfile()
      .subscribe(user => {
        if (!this.authenticationService.isSessionValid()) {
          return;
        }
        this.user = user;
        this.userChecklistService.list().subscribe(list => {
          this.userChecklist = list;
        });
        this.userNotificationService.list({}, { limit: 3, all: 0 }).subscribe(res => {
          this.unreadNotifications = res.notifications;
          this.unreadNotificationsCount = res.notifications.length;
        });
      });
    if (this.authenticationService.client_company_id()) {
      this.companies = this.companies.filter((c) => c.id === this.authenticationService.client_company_id());
    }
    this.fiscalYearService.list({
      company_id: this.getCurrentCompanyId()
    }).subscribe(fiscalYears => {
      const [currentFiscalYear] = fiscalYears.filter((fy) => fy.current);
      this.fiscalYearId = currentFiscalYear?.id;
    });
  }

  logout() {
    this.authenticationService.logout();
    this.location.replaceState('/');
    this.router.navigate(['/login']);
  }

  getCurrentAgencyId() {
    const bureau = this.getBureau();
    if (bureau?.current_agency_id) {
      return bureau?.current_agency_id;
    }
    const company = this.getCompany();
    return company?.current_agency_id;
  }

  selectBureau(company_id: string, company_name: string, bureau_id: string, bureau_name: string) {
    this.authenticationService.setCurrentBureau(company_id, company_name, bureau_id, bureau_name);
    this.location.replaceState('/');
    this.router.navigate(['/']);
  }

  selectGrants(company_id: string, company_name: string) {
    this.authenticationService.setCurrentBureau(company_id, company_name, GRANTS_BUREAU_ID, GRANTS_BUREAU_NAME);
    this.location.replaceState('/');
    this.router.navigate([this.getCompanyBureauUrl('/grants')]);
  }

  companyScopeCount(company: Company): number {
    let count = 0;
    if (this.companyHasBureauAccess(company)) {
      count += (company.bureaus || []).length;
    }
    if (this.companyHasGrantsAccess(company)) {
      count += 1;
    }
    return count;
  }

  showCompanySubmenu(company: Company): boolean {
    return this.authenticationService.is_staff() || this.companyScopeCount(company) > 0;
  }

  directSelectCompanyScope(company: Company) {
    if (this.companyHasBureauAccess(company) && company.bureaus?.length) {
      const [bureau] = company.bureaus;
      this.selectBureau(company.id, company.name, bureau.id, bureau.name);
      return;
    }
    if (this.companyHasGrantsAccess(company)) {
      this.selectGrants(company.id, company.name);
    }
  }

  visibleBureaus(company: Company) {
    if (!this.companyHasBureauAccess(company)) {
      return [];
    }
    return company.bureaus || [];
  }

  setDefaultBureau(company_id: string, bureau_id: string) {
    this.user.default_company_id = company_id;
    this.user.default_bureau_id = bureau_id;
    this.profileService.updateDefaultBureau(this.user)
      .subscribe(user => {
        this.user = user;
      });
  }

  stopPropagation(event: any) {
    event.stopPropagation();
  }

  getBureauImage() {
    // Use the small square logos only (tiny_image): bureau's, then the company's.
    // Skip `.image` — that's the full logo (often with the company name baked in),
    // which doesn't suit the small chip. No tiny_image → building-icon fallback.
    const bureau = this.getBureau();
    if (bureau && bureau.tiny_image) { return bureau.tiny_image; }
    const company = this.getCompany();
    return (company && company.tiny_image) || null;
  }

  // Interactive whenever the user has any companies/bureaus to choose from
  // (matches the original header, which always showed the company/bureau menu).
  canSwitchContext(): boolean {
    return !!(this.companies && this.companies.length);
  }

  onSubmit() {
    this.search = (this.search || '').trim();
    this.router.navigateByUrl('/dummy', { skipLocationChange: true }).then(() => {
      this.router.navigate([this.getCompanyBureauUrl('/events/bureau')], {
        queryParams: { search: this.search }
      });
    });
  }

  changeShadow() {
    this.staffService.updateShadowSchedule({
      id: this.user.staff_id,
      shadowed: !this.user.shadowed
    }).subscribe(res => {
      this.user.shadowed = res.shadowed;
    });
  }
}
